import { useIntl } from 'react-intl'
import styled from 'styled-components'

import ConfirmBackgroundDeleteModal from 'shared/components/modals/ConfirmBackgroundDeleteModal'
import { useBackgroundUploads } from 'shared/hooks/useBackgroundUploads'
import { FileTypes } from 'shared/types/global'
import BackgroundItem from 'shared/ui/BackgroundItem'
import FileUpload from 'shared/ui/FileUpload'
import Flex from 'shared/ui/Flex'
import { Skeleton } from 'shared/ui/Skeleton'
import UploadZone from 'shared/ui/UploadZone'

const Container = styled(Flex)`
  flex-direction: column;
  height: 100%;
`

const ItemsContainer = styled.div`
  flex: 0 1 auto;
  display: grid;
  grid-template-columns: repeat(10, 1fr);
  grid-gap: 10px;
  align-items: center;
  transition: 0.2s;
  padding: 5px;
  overflow: auto;
`

type UploadsTabProps = {
  onSelectedBackgroundChange: (
    data: {
      id: string
    } | null
  ) => void
  hideBackground: boolean
}

const FORMAT_PREFIX =
  'Project.settings.templates.backgrounds.modal.tabs.uploads'

const ChangeBackgroundModalUploadsTab: React.FC<UploadsTabProps> = ({
  onSelectedBackgroundChange,
  hideBackground
}) => {
  const { formatMessage } = useIntl()

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    handleUploadClick,
    handleFileUpload,
    requestImageRemove,
    confirmImageRemove,
    cancelImageRemove,
    isRemoveConfirmationOpen,
    selectedImageId,
    setSelectedImageId,
    imageInputRef,
    isImagesLoading,
    images
  } = useBackgroundUploads({
    onSelectedBackgroundChange
  })

  return (
    <Container {...getRootProps()}>
      <Flex
        marginTop={30}
        marginBottom={25}
        justifyContent="flex-end"
        flex="0 0 auto"
      >
        <UploadZone
          labelId={
            'Project.settings.templates.backgrounds.modal.tabs.uploads.uploadAreaLabel'
          }
          onClick={handleUploadClick}
          isDragActive={isDragActive}
        />
      </Flex>

      <ItemsContainer style={{ opacity: isDragActive ? 0.3 : 1 }}>
        {isImagesLoading
          ? new Array(10)
              .fill(null)
              .map((_, i) => (
                <Skeleton key={i} width={84} height={109} />
              ))
          : images?.map(image => (
              <BackgroundItem
                key={image.id}
                isRemovable
                isSelected={image.id === selectedImageId}
                onClick={() =>
                  setSelectedImageId(
                    selectedImageId === image.id ? null : image.id
                  )
                }
                onRemove={() =>
                  requestImageRemove({
                    backgroundId: image.id,
                    type: image.type
                  })
                }
                src={new URL(
                  image.thumbPath,
                  process.env.REACT_APP_REST_URL
                ).toString()}
              />
            ))}
      </ItemsContainer>
      {images?.length === 0 &&
        formatMessage({ id: `${FORMAT_PREFIX}.noImages` })}

      <FileUpload
        {...getInputProps({
          ref: imageInputRef,
          onSelect: (file: any) => handleFileUpload(file),
          type: FileTypes.image
        })}
      />

      <ConfirmBackgroundDeleteModal
        isOpen={isRemoveConfirmationOpen}
        closeModal={cancelImageRemove}
        onConfirm={confirmImageRemove}
      />
    </Container>
  )
}

export default ChangeBackgroundModalUploadsTab
